import { Form, Formik, FormikHelpers } from 'formik'
import { FormattedMessage, useIntl } from 'react-intl'
import * as Yup from 'yup'
import { Prompt } from 'react-router-dom'

import { useAppDispatch, useAppSelector } from 'app/hooks'
import UserImgContainer from 'components/UserItems/UserImgContainer'
import { TextField } from 'components/Fields'
import { SectionLabel } from 'components/Typography'
import { LoaderOverlay } from 'components/Loader'
import { WhiteBlock } from 'components/Layout'
import { fieldsMessages } from 'components/Fields/messages'

import {
  currentUserSelector,
  passInitResetSelectors,
} from 'features/auth/login/selectors'
import { MyAccountFormValues } from '../types'
import { getMyAccountFormValues } from '../utils'
import { requestUserUpdateThunk } from '../slice'
import { commonMessages } from 'app/i18n/messages'
import { myAccountMessages } from '../messages'
import InputControl from 'components/Input/InputControl'
import {
  MyAccountFormOuter,
  MyAccountFormInner,
  MyAccountFormRight,
  MyAccountFormBtns,
  MyAccountFormBtn,
  MyAccountFormLeft,
} from './styles'

function MyAccountForm() {
  const dispatch = useAppDispatch()
  const { formatMessage } = useIntl()

  const currentUser = useAppSelector(currentUserSelector)
  const isPassResetLoading = useAppSelector(passInitResetSelectors.isFetching)

  const initialMyAccountValues = getMyAccountFormValues(currentUser)

  const { email } = currentUser

  const handleSubmit = async (
    values: MyAccountFormValues,
    formikHelpers: FormikHelpers<MyAccountFormValues>
  ) => {
    try {
      await dispatch(requestUserUpdateThunk(values))
    } catch (err) {
      return formikHelpers.resetForm()
    }
    formikHelpers.resetForm({ values })
  }

  return (
    <Formik
      initialValues={initialMyAccountValues}
      onSubmit={handleSubmit}
      validationSchema={Yup.object().shape({
        firstName: Yup.string()
          .trim()
          .required(formatMessage(fieldsMessages.requiredField)),
        lastName: Yup.string()
          .trim()
          .required(formatMessage(fieldsMessages.requiredField)),
      })}
    >
      {(formikProps) => {
        return (
          <>
            <Prompt
              when={formikProps.dirty}
              message={formatMessage(commonMessages.promptUnsavedForm)}
            />
            <Form>
              {(formikProps.isSubmitting || isPassResetLoading) && (
                <LoaderOverlay />
              )}
              <WhiteBlock>
                <SectionLabel>
                  <FormattedMessage {...myAccountMessages.profileInfo} />
                </SectionLabel>
                <MyAccountFormOuter>
                  <MyAccountFormInner>
                    <MyAccountFormLeft>
                      <UserImgContainer
                        profilePicUrl={currentUser.profilePicUrl}
                      />
                    </MyAccountFormLeft>
                    <MyAccountFormRight>
                      <div>
                        <TextField
                          name="firstName"
                          placeholder={formatMessage(
                            myAccountMessages.firstName
                          )}
                          maxLength={30}
                          showLabel
                        />
                      </div>
                      <div>
                        <TextField
                          name="lastName"
                          placeholder={formatMessage(
                            myAccountMessages.lastName
                          )}
                          maxLength={30}
                          showLabel
                        />
                      </div>
                      <div>
                        <InputControl
                          placeholder={formatMessage(myAccountMessages.email)}
                          defaultValue={email}
                          disabled
                          showLabel
                        />
                      </div>
                    </MyAccountFormRight>
                  </MyAccountFormInner>
                  {formikProps.dirty && (
                    <MyAccountFormBtns>
                      <MyAccountFormBtn
                        type="button"
                        variant="secondary"
                        onClick={(ev) => {
                          ev.preventDefault()
                          ev.stopPropagation()
                          formikProps.resetForm()
                        }}
                      >
                        <FormattedMessage {...commonMessages.cancel} />
                      </MyAccountFormBtn>
                      <MyAccountFormBtn
                        type="submit"
                        variant="primary"
                        disabled={!formikProps.isValid}
                      >
                        <FormattedMessage {...commonMessages.save} />
                      </MyAccountFormBtn>
                    </MyAccountFormBtns>
                  )}
                </MyAccountFormOuter>
              </WhiteBlock>
            </Form>
          </>
        )
      }}
    </Formik>
  )
}

export default MyAccountForm
